import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Users,
  MapPin,
  Layers,
  Globe2,
  Smartphone,
  Search,
  Map,
  Briefcase,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  Scale,
  Siren,
  GraduationCap,
  HeartHandshake,
  FolderOpen,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button } from '../components';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import { EmployerProfilePage } from './EmployerProfilePage';
import { EmployerOrgStructuur } from '../components/EmployerOrgStructuur';
import { EmployerTeamPage } from './EmployerTeamPage';
import { EmployerPoolSetupPage } from './EmployerPoolSetupPage';
import { EmployerOnderhandelingen } from '../components/EmployerOnderhandelingen';
import { EmployerCrisisPool } from '../components/EmployerCrisisPool';
import { EmployerVertrouwenspersoonModule } from '../components/EmployerVertrouwenspersoonModule';
import { PayrollBridgeWizard } from '../components/PayrollBridgeWizard';
import { AcademyTab, PORTAAL_QUERY_KEY } from './EmployerMedewerkersportaalPage';
import { getEmployerPortaal } from '../services/medewerkerApi';
import { EmployerContractenDocumenten } from '../components/EmployerContractenDocumenten';

type MijnBelagomTab = 'profiel' | 'contracten' | 'toegang' | 'pools' | 'werkgebied' | 'onderhandelingen' | 'portaal' | 'flexbudget' | 'crisis' | 'vertrouwenspersoon';

/** Oude ?tab=-waardes blijven werken: Juridisch & Support is opgegaan in Contracten & Documenten. */
const LEGACY_TABS: Record<string, MijnBelagomTab> = { juridisch: 'contracten' };

/**
 * Linkermenu Mijn Belagom. Volgorde is de inrichtingsflow van de opdrachtgever:
 * 1. profiel (+ contracten & documenten, direct eronder) → 2. toegang → 3. menukaart (welke professionals) →
 * 4. werkgebied (binnen welke straal zoeken) → 5. onderhandelingen (komen we op prijs uit) →
 * 6. medewerkerportaal (uit welke professionals medewerkers kiezen) →
 * 7. L&O-flexbudget (opleidingsbudgetten) → 8. crisisdienst & garantiefonds →
 * 9. vertrouwenspersoon: Waakvlam (paraatheid, 12-uurs SLA) en zichtbaarheidsmandaten.
 */
const TABS: Array<{
  key: MijnBelagomTab;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  adminOnly?: boolean;
}> = [
  { key: 'profiel', label: 'Organisatieprofiel', description: 'De visitekaart van uw organisatie', icon: Building2 },
  { key: 'contracten', label: 'Contracten & Documenten', description: 'Getekende overeenkomsten, organisatiedocumenten en klachtenreglement', icon: FolderOpen },
  { key: 'toegang', label: 'Toegangsbeheer & Rechten', description: "Collega's uitnodigen en rollen beheren", icon: Users, adminOnly: true },
  { key: 'pools', label: 'Pool-Configurator', description: 'Welke professionals uw organisatie nodig heeft (uw menukaart)', icon: Layers, adminOnly: true },
  { key: 'werkgebied', label: 'Werkgebied & Radius', description: 'Tot welke afstand professionals gezocht worden', icon: MapPin, adminOnly: true },
  { key: 'onderhandelingen', label: 'Status onderhandelingen', description: 'Voortgang van benaderde professionals en hun tariefonderhandeling', icon: Scale, adminOnly: true },
  { key: 'portaal', label: 'Inrichten medewerkersportaal', description: 'Het anonieme medewerkerportaal voor uw medewerkers', icon: Smartphone, adminOnly: true },
  { key: 'flexbudget', label: 'Inrichten L&O-flexbudget', description: 'Opleidingsbudgetten, WKR-ruimte en Academy-model voor uw medewerkers', icon: GraduationCap, adminOnly: true },
  { key: 'crisis', label: 'Crisisdienst & Garantiefonds', description: '24/7 crisis-pool per vestiging, No-Claim tegoed en de alarmknop', icon: Siren, adminOnly: true },
  { key: 'vertrouwenspersoon', label: 'Vertrouwenspersoon: Waakvlam & Zichtbaarheid', description: 'Paraatheidsfee met 12-uurs reactiegarantie en budget voor zichtbaarheidsuren op locatie', icon: HeartHandshake, adminOnly: true },
];

const RADIUS_PRESETS = [25, 50, 75, 100];

interface BeschikbaarheidDiscipline {
  professional_type: string;
  label: string;
  gewenst: number;
  beschikbaar: number;
  tekort: number;
}

interface Beschikbaarheid {
  radius_km: number | null;
  landelijk: boolean;
  vestiging_bekend: boolean;
  pool_ingericht: boolean;
  totaal_beschikbaar: number;
  totaal_gewenst: number;
  disciplines: BeschikbaarheidDiscipline[];
}

/** Kleine debounce-hook: de slider vuurt per stap, de telling pas na rust. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Live-telling naast de slider: hoeveel professionals uit de eigen menukaart
 * zitten binnen de (nog niet opgeslagen) radius. Tekorten worden rood
 * gemarkeerd zodat de opdrachtgever direct ziet of hij verder moet zoeken.
 */
const BeschikbaarheidPanel: React.FC<{ radius: number | null }> = ({ radius }) => {
  const { t } = useTranslation('employer');
  const debouncedRadius = useDebounced(radius, 250);

  const { data, isFetching, isError } = useQuery<Beschikbaarheid>({
    queryKey: ['werkgebied-beschikbaarheid', debouncedRadius],
    queryFn: async () => {
      const params = debouncedRadius ? `?radius_km=${debouncedRadius}` : '';
      return (await api.get(`/employer/werkgebied/beschikbaarheid${params}`)).data?.data;
    },
    placeholderData: (prev) => prev,
    staleTime: 60 * 1000,
  });

  if (isError) {
    return (
      <p className="text-sm text-red-600">
        {t('werkgebied.tellingFout', 'Beschikbaarheid kon niet worden berekend.')}
      </p>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('werkgebied.tellingLaden', 'Beschikbaarheid berekenen…')}
      </div>
    );
  }

  if (!data.pool_ingericht) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <p className="font-medium mb-1">
          {t('werkgebied.geenPoolTitel', 'Nog geen menukaart ingericht')}
        </p>
        <p className="mb-2">
          {t(
            'werkgebied.geenPoolUitleg',
            'Bepaal eerst in de Pool-Configurator welke professionals uw organisatie nodig heeft. Daarna ziet u hier direct hoeveel daarvan binnen uw werkgebied beschikbaar zijn.'
          )}
        </p>
        <Link to="/employer/mijn-belagom?tab=pools" className="font-medium text-amber-900 underline">
          {t('werkgebied.naarPool', 'Naar de Pool-Configurator')}
        </Link>
      </div>
    );
  }

  const tekortDisciplines = data.disciplines.filter((d) => d.tekort > 0);
  const dekkingOk = tekortDisciplines.length === 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">
            {t('werkgebied.tellingTitel', 'Beschikbaar uit uw menukaart')}
          </p>
          <p className="text-xs text-gray-500">
            {data.landelijk
              ? t('werkgebied.tellingLandelijk', 'Heel Nederland')
              : t('werkgebied.tellingBinnen', 'Binnen {{km}} km van uw hoofdvestiging', { km: data.radius_km })}
            {isFetching && <Loader2 className="inline w-3 h-3 ml-2 animate-spin" />}
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-700">{data.totaal_beschikbaar}</div>
          <div className="text-xs text-gray-500">
            {t('werkgebied.tellingProfessionals', 'professionals')}
          </div>
        </div>
      </div>

      {!data.vestiging_bekend && !data.landelijk && (
        <p className="text-xs text-amber-700 mb-3 flex items-start gap-1">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          {t(
            'werkgebied.geenVestiging',
            'Vestigingspostcode ontbreekt of is niet te vinden; de telling is nu landelijk. Vul het organisatieprofiel aan.'
          )}
        </p>
      )}

      <ul className="divide-y divide-gray-200">
        {data.disciplines.map((d) => {
          const tekort = d.tekort > 0;
          return (
            <li key={d.professional_type} className="flex items-center justify-between py-1.5 text-sm">
              <span className="flex items-center gap-2 text-gray-800">
                {tekort ? (
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                )}
                {d.label}
              </span>
              <span className={`font-medium tabular-nums ${tekort ? 'text-red-600' : 'text-gray-900'}`}>
                {d.beschikbaar} / {d.gewenst}
                {tekort && (
                  <span className="ml-2 text-xs font-normal text-red-500">
                    {t('werkgebied.tekortBadge', 'tekort {{n}}', { n: d.tekort })}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      <p className={`mt-3 text-xs ${dekkingOk ? 'text-emerald-700' : 'text-red-600'}`}>
        {dekkingOk
          ? t('werkgebied.dekkingOk', 'Alle disciplines van uw menukaart zijn binnen dit werkgebied gedekt.')
          : t('werkgebied.dekkingTekort', '{{n}} discipline(s) met een tekort: vergroot de radius of zoek verder via de opties hieronder.', { n: tekortDisciplines.length })}
      </p>
    </div>
  );
};

/** Tab 4: Werkgebied & Radius-slider. NULL = landelijk; voedt de Haversine-afstandsfilter. */
const WerkgebiedTab: React.FC = () => {
  const { t } = useTranslation('employer');
  const queryClient = useQueryClient();
  const [radius, setRadius] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ['employer-profile'],
    queryFn: async () => (await api.get('/employer/profile')).data?.data,
  });

  useEffect(() => {
    if (profile && !loaded) {
      setRadius(profile.werkgebied_radius_km ?? null);
      setLoaded(true);
    }
  }, [profile, loaded]);

  const saveMutation = useMutation({
    mutationFn: async (value: number | null) =>
      api.patch('/employer/profile/werkgebied', { werkgebied_radius_km: value }),
    onSuccess: () => {
      toast.success(t('werkgebied.saved', 'Werkgebied opgeslagen'));
      queryClient.invalidateQueries({ queryKey: ['employer-profile'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || t('werkgebied.saveError', 'Opslaan mislukt'));
    },
  });

  const isLandelijk = radius === null;
  const opgeslagenRadius: number | null = profile?.werkgebied_radius_km ?? null;
  const ongewijzigd = loaded && radius === opgeslagenRadius;

  return (
    <div className="max-w-5xl">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              {t('werkgebied.title', 'Werkgebied & Radius')}
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              {t(
                'werkgebied.intro',
                'Stel in tot welke afstand rondom uw hoofdvestiging professionals gezocht en gematcht worden. Terwijl u schuift ziet u rechts hoeveel professionals van uw menukaart binnen dit werkgebied beschikbaar zijn.'
              )}
            </p>

            {profile && (
              <div className="flex items-center gap-2 text-sm text-gray-700 mb-6">
                <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <span>
                  {t('werkgebied.vestiging', 'Hoofdvestiging')}:{' '}
                  <strong>
                    {profile.postcode || profile.plaats
                      ? [profile.postcode, profile.plaats].filter(Boolean).join(' ')
                      : t('werkgebied.geenAdres', 'nog geen adres ingevuld — vul eerst het organisatieprofiel in')}
                  </strong>
                </span>
              </div>
            )}

            {/* Presets */}
            <div className="flex flex-wrap gap-2 mb-6">
              {RADIUS_PRESETS.map((km) => (
                <button
                  key={km}
                  type="button"
                  onClick={() => setRadius(km)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                    radius === km
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {km} km
                </button>
              ))}
              <button
                type="button"
                onClick={() => setRadius(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors inline-flex items-center gap-1.5 ${
                  isLandelijk
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Globe2 className="w-4 h-4" />
                {t('werkgebied.landelijk', 'Landelijk')}
              </button>
            </div>

            {/* Slider */}
            <div className={isLandelijk ? 'opacity-40 pointer-events-none' : ''}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('werkgebied.sliderLabel', 'Fijnafstelling')}
                </label>
                <span className="text-sm font-semibold text-blue-700">
                  {isLandelijk ? '—' : `${radius} km`}
                </span>
              </div>
              <input
                type="range"
                min={5}
                max={150}
                step={5}
                value={radius ?? 50}
                onChange={(e) => setRadius(Number(e.target.value))}
                className="w-full accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>5 km</span>
                <span>150 km</span>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <Button onClick={() => saveMutation.mutate(radius)} loading={saveMutation.isPending}>
                {t('werkgebied.save', 'Werkgebied opslaan')}
              </Button>
              <span className="text-xs text-gray-500">
                {isLandelijk
                  ? t('werkgebied.uitlegLandelijk', 'Professionals uit heel Nederland worden meegenomen.')
                  : t('werkgebied.uitlegRadius', 'Alleen professionals binnen deze reisradius worden gematcht.', { km: radius })}
              </span>
            </div>
          </Card>

          {/* Vervolgstappen: profielen bekijken, of bij tekort verder zoeken */}
          <Card className="mt-6">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              {t('werkgebied.vervolgTitel', 'En dan: profielen bekijken')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {ongewijzigd
                ? t(
                    'werkgebied.vervolgUitleg',
                    'Uw werkgebied is opgeslagen. Bekijk nu de profielen van de beschikbare professionals. Komt u toch tekort? Zoek dan zelf op de kaart van Nederland, plaats een zoekopdracht op criteria of zet een vacature op het platform.'
                  )
                : t(
                    'werkgebied.vervolgEerstOpslaan',
                    'Sla het werkgebied eerst op; de zoekfuncties hieronder gebruiken de opgeslagen radius.'
                  )}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link
                to="/employer/search-professionals"
                className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 hover:bg-blue-100 transition-colors"
              >
                <Users className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>
                  <span className="block text-sm font-medium text-blue-900">
                    {t('werkgebied.ctaProfielen', 'Beschikbare profielen bekijken')}
                  </span>
                  <span className="block text-xs text-blue-700">
                    {t('werkgebied.ctaProfielenSub', 'Professionals van uw menukaart binnen uw werkgebied')}
                  </span>
                </span>
              </Link>
              <Link
                to="/employer/map-search"
                className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 hover:bg-gray-50 transition-colors"
              >
                <Map className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    {t('werkgebied.ctaKaart', 'Zelf zoeken op de kaart')}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {t('werkgebied.ctaKaartSub', 'Heel Nederland, met dekking per discipline')}
                  </span>
                </span>
              </Link>
              <Link
                to="/employer/search-professionals"
                className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 hover:bg-gray-50 transition-colors"
              >
                <Search className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    {t('werkgebied.ctaCriteria', 'Zoekopdracht op criteria')}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {t('werkgebied.ctaCriteriaSub', 'BeLagom zoekt mee op specialisatie, ervaring en beschikbaarheid')}
                  </span>
                </span>
              </Link>
              <Link
                to="/opdrachten/nieuw"
                className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 hover:bg-gray-50 transition-colors"
              >
                <Briefcase className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0" />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    {t('werkgebied.ctaVacature', 'Vacature plaatsen')}
                  </span>
                  <span className="block text-xs text-gray-500">
                    {t('werkgebied.ctaVacatureSub', 'Professionals op het platform reageren zelf')}
                  </span>
                </span>
              </Link>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-6">
            <BeschikbaarheidPanel radius={radius} />
          </div>
        </div>
      </div>
    </div>
  );
};

interface PoolSetupSummary {
  config: { vitaliteits_niveau: 'A' | 'B' | 'C' | null; arbo_module_actief: boolean; afgerond_op: string | null } | null;
  selectie: { professional_type: string; bron: string; gewenste_experts: number }[];
  disciplines: { professional_type: string; label: string; category: string; is_medewerker_gericht: boolean }[];
}

/**
 * Tab 6: medewerkersportaal. Toont welke medewerkergerichte disciplines uit
 * de menukaart in het anonieme portaal zichtbaar worden en hoe medewerkers
 * toegang krijgen (toegangscode/uitnodiging + eigen account onder pseudoniem). De inhoud volgt de
 * Pool-Configurator; hier wordt niets apart opgeslagen.
 */
/** Tab 7: L&O-flexbudget. Hergebruikt de Academy-instellingen van het medewerkersportaal (zelfde query-key, dus één bron van waarheid). */
const FlexbudgetTab: React.FC = () => {
  const { t } = useTranslation('employer');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: PORTAAL_QUERY_KEY,
    queryFn: getEmployerPortaal,
  });
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-8">
        <Loader2 className="w-5 h-5 animate-spin" />
        {t('mijnBelagom.flexbudgetLaden', 'Flexbudget-instellingen laden…')}
      </div>
    );
  }
  if (isError || !data) {
    return (
      <Card>
        <p className="text-red-600">
          {(error as any)?.response?.data?.message || t('mijnBelagom.flexbudgetFout', 'Flexbudget-instellingen konden niet worden geladen.')}
        </p>
      </Card>
    );
  }
  return (
    <div className="space-y-6">
      {/* Onderdeel 7: Excel Payroll Bridge — personeelsdata, kolom-mapper en budget-allocatie */}
      <PayrollBridgeWizard />
      <AcademyTab data={data} />
    </div>
  );
};

const PortaalTab: React.FC = () => {
  const { t } = useTranslation('employer');
  const { data, isLoading } = useQuery<PoolSetupSummary>({
    queryKey: ['pool-setup'],
    queryFn: async () => (await api.get('/employer/pool-setup')).data?.data,
  });

  const zichtbaar = useMemo(() => {
    if (!data) return [];
    const gekozen = new Set(data.selectie.map((s) => s.professional_type));
    return data.disciplines.filter((d) => d.is_medewerker_gericht && gekozen.has(d.professional_type));
  }, [data]);

  const nietZichtbaar = useMemo(() => {
    if (!data) return [];
    const gekozen = new Set(data.selectie.map((s) => s.professional_type));
    return data.disciplines.filter((d) => !d.is_medewerker_gericht && gekozen.has(d.professional_type));
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  const poolIngericht = !!data && data.selectie.length > 0;

  return (
    <div className="max-w-4xl space-y-6">
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {t('portaal.title', 'Inrichten medewerkersportaal')}
        </h2>
        <p className="text-sm text-gray-600">
          {t(
            'portaal.intro',
            'Uw medewerkers krijgen een mobiel, anoniem portaal waarin zij zelf een afspraak inplannen bij de medewerkergerichte professionals uit uw menukaart. De inhoud van het portaal volgt automatisch uit de Pool-Configurator.'
          )}
        </p>
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <ShieldCheck className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <span>
            {t(
              'portaal.privacy',
              'Medewerkers registreren zich met de toegangscode of persoonlijke uitnodiging van uw organisatie, met een eigen e-mailadres en wachtwoord. Ze gebruiken het portaal onder een pseudoniem: u ziet nooit wie een afspraak maakt.'
            )}
          </span>
        </div>
      </Card>

      {!poolIngericht ? (
        <Card>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            <p className="font-medium mb-1">{t('portaal.geenPoolTitel', 'Nog geen menukaart ingericht')}</p>
            <p className="mb-2">
              {t('portaal.geenPoolUitleg', 'Het portaal toont de medewerkergerichte disciplines uit uw menukaart. Richt eerst de Pool-Configurator in.')}
            </p>
            <Link to="/employer/mijn-belagom?tab=pools" className="font-medium text-amber-900 underline">
              {t('portaal.naarPool', 'Naar de Pool-Configurator')}
            </Link>
          </div>
        </Card>
      ) : (
        <>
          <Card>
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              {t('portaal.zichtbaarTitel', 'Zichtbaar voor medewerkers in het portaal')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('portaal.zichtbaarUitleg', 'Deze disciplines kunnen medewerkers zelf, anoniem, boeken.')}
            </p>
            {zichtbaar.length === 0 ? (
              <p className="text-sm text-amber-700">
                {t('portaal.geenZichtbaar', 'Uw menukaart bevat nog geen medewerkergerichte discipline. Voeg er een toe in de Pool-Configurator.')}
              </p>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {zichtbaar.map((d) => (
                  <li key={d.professional_type} className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-gray-900">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    {d.label}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {nietZichtbaar.length > 0 && (
            <Card>
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                {t('portaal.organisatieTitel', 'Alleen via de organisatie (niet in het portaal)')}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('portaal.organisatieUitleg', 'Organisatiegerichte disciplines zet u zelf in via trajecten en opdrachten; medewerkers zien deze niet.')}
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {nietZichtbaar.map((d) => (
                  <li key={d.professional_type} className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
                    <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    {d.label}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <div className="flex flex-wrap gap-3">
            <Link to="/employer/mijn-belagom?tab=pools">
              <Button variant="outline">
                <Layers className="w-4 h-4 mr-2" />
                {t('portaal.menukaartAanpassen', 'Menukaart aanpassen')}
              </Button>
            </Link>
            <Link to="/employer/mijn-belagom?tab=werkgebied">
              <Button variant="outline">
                <MapPin className="w-4 h-4 mr-2" />
                {t('portaal.werkgebiedAanpassen', 'Werkgebied aanpassen')}
              </Button>
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export const MijnBelagomPage: React.FC = () => {
  const { t } = useTranslation('employer');
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const isHoofdgebruiker = user?.role === 'opdrachtgever';
  // Toegangsbeheer ook voor Global Admin en Vestigingsmanager (eigen vestiging); afdelingschef niet
  const magToegangsbeheer =
    isHoofdgebruiker ||
    user?.employer_toegangsniveau === 'global_admin' ||
    user?.employer_toegangsniveau === 'vestigingsmanager';
  const visibleTabs = TABS.filter(
    (tab) => !tab.adminOnly || isHoofdgebruiker || (tab.key === 'toegang' && magToegangsbeheer)
  );

  const rawTab = searchParams.get('tab');
  const paramTab = (rawTab && LEGACY_TABS[rawTab]) || (rawTab as MijnBelagomTab | null);
  const activeTab: MijnBelagomTab =
    paramTab && visibleTabs.some((tab) => tab.key === paramTab) ? paramTab : 'profiel';

  useCockpitTitle(
    t('mijnBelagom.title', 'Mijn Cockpit'),
    t('mijnBelagom.subtitle', 'De centrale beheer- en inrichtingsomgeving van uw organisatie')
  );

  const selectTab = (tab: MijnBelagomTab) => {
    setSearchParams({ tab }, { replace: true });
  };

  return (
    <div className="flex flex-col md:flex-row md:h-full">
      {/* Stappenmenu in de linkerkolom (zelfde breedte als de logo-kolom in de header), genummerd in de volgorde van de inrichtingsflow */}
      <nav className="md:w-64 flex-shrink-0 bg-white border-b md:border-b-0 md:border-r border-gray-200 md:overflow-y-auto">
        <div className="p-3 md:p-4">
          <h2 className="hidden md:block text-lg font-bold text-gray-900 mb-1">
            {t('mijnBelagom.title', 'Mijn Cockpit')}
          </h2>
          <p className="hidden md:block text-xs text-gray-500 mb-4">
            {t('mijnBelagom.menuSubtitle', 'Richt uw organisatie stap voor stap in')}
          </p>
          <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-1 md:pb-0">
            {visibleTabs.map((tab, index) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => selectTab(tab.key)}
                  className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-colors flex-shrink-0 md:flex-shrink ${
                    isActive
                      ? 'bg-blue-50 border-blue-200 text-blue-700'
                      : 'bg-white border-transparent text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span
                    className={`hidden md:flex w-6 h-6 rounded-full items-center justify-center text-xs font-semibold flex-shrink-0 ${
                      isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium whitespace-nowrap md:whitespace-normal">{t(`mijnBelagom.tab_${tab.key}`, tab.label)}</span>
                    <span className="hidden md:block text-xs text-gray-500 mt-0.5">
                      {t(`mijnBelagom.tabDesc_${tab.key}`, tab.description)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Tab-content */}
      <div className="flex-1 min-w-0 md:overflow-y-auto px-4 sm:px-6 py-6">
        {activeTab === 'profiel' && (
          <>
            <EmployerProfilePage />
            {/* Vestigingen-Matrix + afdelingen: structuur alleen door hoofdgebruiker te beheren */}
            {isHoofdgebruiker && <EmployerOrgStructuur />}
          </>
        )}
        {activeTab === 'toegang' && <EmployerTeamPage />}
        {activeTab === 'pools' && <EmployerPoolSetupPage />}
        {activeTab === 'onderhandelingen' && <EmployerOnderhandelingen />}
        {activeTab === 'werkgebied' && <WerkgebiedTab />}
        {activeTab === 'portaal' && <PortaalTab />}
        {activeTab === 'flexbudget' && <FlexbudgetTab />}
        {activeTab === 'crisis' && <EmployerCrisisPool />}
        {activeTab === 'vertrouwenspersoon' && <EmployerVertrouwenspersoonModule />}
        {activeTab === 'contracten' && <EmployerContractenDocumenten />}
      </div>
    </div>
  );
};
